import instance from '@/utils/axios';

/**
 * 获取病原体总数
 * @param {Object} data - 请求参数对象
 * @param {number} data.beginTimestamp - 开始时间戳
 * @param {number} data.endTimestamp - 结束时间戳
 * @param {number} data.logTimestamp - 日志时间戳
 * @param {Array<string>} data.officeIds - 科室ID列表
 * @param {string[]} data.pathogenTypes - 病原体类型
 * @param {string} data.sn - 序列号
 * @returns {Promise<{date: string, pathogen: Object}[]>} 返回一个包含日期和病原体数量的对象
 * example:
 * {
 *   "date": "2023-11-10",
 *   "pathogen": {
 *     "新型冠状病毒": 2,
 *     "乙型流感病毒": 2,
 *     "甲型流感病毒": 2
 *   }
 * }
 */
export function getPathogenCountByDay(data) {
  return instance.post('/rtEao/queryPathogenCountByDay', data);
}

/**
 * 获取各科室病原体数量统计
 * @param {Object} data - 请求参数对象
 * @param {number} data.beginTimestamp - 开始时间戳
 * @param {number} data.endTimestamp - 结束时间戳
 * @param {number} data.logTimestamp - 日志时间戳
 * @param {Array<string>} data.officeIds - 科室ID列表
 * @param {string[]} data.pathogenTypes - 病原体类型
 * @param {string} data.sn - 序列号
 * @returns {Promise<{pathogenName: string, value: number}[]>} 返回一个包含病原体名称和数量的数组
 * example:
 * [
 *   {
 *     "pathogenName": "甲型流感病毒",
 *     "value": 61
 *   }
 * ]
 */
export function getPathogenCountByOffice(data) {
  return instance.post('/rtEao/queryPathogenCountByOffice', data);
}

/**
 * 获取各科室样本数量统计
 * @param {Object} data - 请求参数对象
 * @param {number} data.beginTimestamp - 开始时间戳
 * @param {number} data.endTimestamp - 结束时间戳
 * @param {number} data.logTimestamp - 日志时间戳
 * @param {Array<string>} data.officeIds - 科室ID列表
 * @param {string} data.sn - 序列号
 * @returns {Promise<{officeName: string, bad: number, good: number}[]>} 返回一个包含科室名称、阳性样本数和阴性样本数的数组
 * example:
 * [
 *   {
 *     "officeName": "北京火车站",
 *     "bad": 42,
 *     "good": 60
 *   }
 * ]
 */
export function getSampleCountByOffice(data) {
  return instance.post('/rtEao/querySampleCountByOffice', data);
}

/**
 * 获取各机构状态
 * @param {Object} data - 请求参数对象
 * @param {number} data.beginTimestamp - 开始时间戳
 * @param {number} data.endTimestamp - 结束时间戳
 * @param {number} data.logTimestamp - 日志时间戳
 * @param {Array<string>} data.officeIds - 科室ID列表
 * @param {string} data.sn - 序列号
 * @returns {Promise<Array<{ [officeName: string]: number }>>} 返回一个包含科室名称和状态的数组
 * example:
 * [
 *   {
 *     "北京火车站": "1"
 *   },
 *   {
 *     "西单地铁": "1"
 *   }
 * ]
 */
export function getOfficesState(data) {
  return instance.post('/rtEao/queryOfficeState', data);
}

/**
 * 获取各科室设备检测数量统计
 * @param {Object} data - 请求参数对象
 * @param {number} data.beginTimestamp - 开始时间戳
 * @param {number} data.endTimestamp - 结束时间戳
 * @param {number} data.logTimestamp - 日志时间戳
 * @param {string} data.officeId - 科室ID
 * @param {Array<string>} data.officeIds - 科室ID列表
 * @param {string} data.pathogenType - 病原体类型
 * @param {string} data.sn - 序列号
 * @returns {Promise<{name: string, sn: string, total: number, bad: number}[]>} 返回一个包含设备名称、序列号、总检测数和阳性数的数组
 * example:
 * [
 *   {
 *     "name": "XD01",
 *     "sn": "XD01",
 *     "total": 81,
 *     "bad": 18
 *   }
 * ]
 */
export function getDeviceTestCountByOfficeId(data) {
  return instance.post('/rtEao/queryDeviceTestCountByOfficeId', data);
}

/**
 * 获取AI预测结果
 * @param {Object} data - 请求参数对象
 * @param {number} data.beginTimestamp - 开始时间戳
 * @param {number} data.endTimestamp - 结束时间戳
 * @param {number} data.logTimestamp - 日志时间戳
 * @param {string} data.officeId - 科室ID
 * @param {Array<string>} data.officeIds - 科室ID列表
 * @param {string} data.pathogenType - 病原体类型
 * @param {string} data.sn - 序列号
 * @returns {Promise<Array<{
 *   officeId: string,
 *   officeName: string,
 *   list: Array<{
 *     pathogenName: string,
 *     past: Array<{
 *       dt: string,
 *       pathogenState: string,
 *       proportion: number,
 *       trend: string
 *     }>,
 *     prediction: Array<{
 *       dt: string,
 *       pathogenState: string,
 *       proportion: number,
 *       trend: string
 *     }>
 *   }>
 * }>>} 返回一个包含科室ID、科室名称和预测结果列表的数组
 * example:
 * [
 *   {
 *     "officeId": "86e8999bef3e480e8604df566c60d053",
 *     "officeName": "西单地铁",
 *     "list": [
 *       {
 *         "pathogenName": "新型冠状病毒",
 *         "past": [
 *           {
 *             "dt": "2023-10-02",
 *             "pathogenState": "0",
 *             "proportion": 0,
 *             "trend": "1"
 *           }
 *         ],
 *         "prediction": [
 *           {
 *             "dt": "2024-03-09",
 *             "pathogenState": "1",
 *             "proportion": 18.938604,
 *             "trend": "2"
 *           }
 *         ]
 *       }
 *     ]
 *   }
 * ]
 */
export function getPredictAIResult(data) {
  return instance.post('/rtEao/queryPredictAIResult', data);
}

/**
 * 获取报告列表
 * @param {Object} data - 请求参数对象
 * @param {number} data.beginTimestamp - 开始时间戳
 * @param {number} data.endTimestamp - 结束时间戳
 * @param {number} data.limit - 每页数量
 * @param {Array<string>} data.officeIds - 科室ID列表
 * @param {number} data.offset - 偏移量
 * @param {string[]} data.pathogenTypes - 病原体类型列表

 * @returns {Promise<import('types/api/index').TPageResult<Array<{
 *   id: string,
 *   samplePoint: string,
 *   company: string,
 *   positiveNum: number,
 *   reportTime: string,
 *   createBy: string,
 *   createDate: string,
 *   updateBy: string,
 *   updateDate: string,
 *   remarks: string | null,
 *   delFlag: string,
 *   sampleStartTime: string,
 *   sampleEndTime: string,
 *   sn: string,
 *   pathogenValue: number,
 *   pathogenName: string,
 *   pathogenState: string,
 *   sampleTestOrg: string,
 *   sampleTestDevice: string,
 *   sampleDetectionReagent: string,
 *   sampleTestTime: string,
 *   sampleVideo: string | null
 * }>>>} 返回报告列表信息
 * example:
 * {
 *   "pageNum": 0,
 *   "pageSize": 0,
 *   "size": 5,
 *   "startRow": 1,
 *   "endRow": 5,
 *   "total": 5,
 *   "pages": 0,
 *   "list": [
 *     {
 *       "id": "1",
 *       "samplePoint": "北京火车站",
 *       "company": "华泰诺安",
 *       "positiveNum": 10,
 *       "reportTime": "2023-10-10 10:00:00",
 *       "createBy": "admin",
 *       "createDate": "2023-10-10 10:00:00",
 *       "updateBy": "admin",
 *       "updateDate": "2023-10-10 10:00:00",
 *       "remarks": null,
 *       "delFlag": "0",
 *       "sampleStartTime": "2023-10-10 08:00:00",
 *       "sampleEndTime": "2023-10-10 09:00:00",
 *       "sn": "BJZ001",
 *       "pathogenValue": 37.5,
 *       "pathogenName": "新型冠状病毒",
 *       "pathogenState": "1",
 *       "sampleTestOrg": "华泰诺安实验室",
 *       "sampleTestDevice": "SLAN®-96P 全自动医用 PCR 分析系统",
 *       "sampleDetectionReagent": "圣湘十二项呼吸道病原体核酸检测试剂盒",
 *       "sampleTestTime": "2023-10-10 10:00:00",
 *       "sampleVideo": null
 *     }
 *   ]
 * }
 */
export function getReportList(data) {
  return instance.post('rtEao/queryReportList', data);
}

/**
 * 获取AI训练列表
 * @param {Object} data - 请求参数对象
 * @returns {Promise<import('types/api/index').TResult<{
 *   id: string,
 *   version: string,
 *   trainingTime: string,
 *   trainingRange: string,
 *   isCurrVersion: string,
 *   createBy: string,
 *   createDate: string,
 *   updateBy: string,
 *   updateDate: string,
 *   remarks: string|null,
 *   delFlag: string
 * }[]>>} 返回AI训练列表信息
 * example:
 * [
 *   {
 *     "id": "55e2cf62-5ef1-4488-8ebc-82f3f7b73561",
 *     "version": "V2.50",
 *     "trainingTime": "1760162134000",
 *     "trainingRange": "新冠预测",
 *     "isCurrVersion": "1",
 *     "createBy": "9f4241ae755243dd96444201eae18ca5",
 *     "createDate": "1760162134000",
 *     "updateBy": "9f4241ae755243dd96444201eae18ca5",
 *     "updateDate": "1760162164000",
 *     "remarks": null,
 *     "delFlag": "0"
 *   }
 * ]
 */
export function getAiTrainingList(data) {
  return instance.post('/rtEao/queryAiTrainingList', data);
}

/**
 * 插入AI训练记录
 * @param {Object} data - 请求参数对象
 * @param {string} data.createBy - 创建人
 * @param {string} data.createDate - 创建日期
 * @param {string} data.delFlag - 删除标志
 * @param {string} data.id - ID
 * @param {string} data.isCurrVersion - 是否当前版本
 * @param {string} data.remarks - 备注
 * @param {string} data.trainingRange - 训练范围
 * @param {string} data.trainingTime - 训练时间
 * @param {string} data.updateBy - 更新人
 * @param {string} data.updateDate - 更新日期
 * @param {string} data.version - 版本
 * @returns {Promise<any>} 返回插入结果
 */
export function insertAiTraining(data) {
  return instance.post('/rtEao/insertAiTraining', data);
}

/**
 * 更新AI训练记录为当前版本
 * @param {Object} data - 请求参数对象
 * @param {string} data.createBy - 创建人
 * @param {string} data.createDate - 创建日期
 * @param {string} data.delFlag - 删除标志
 * @param {string} data.id - ID
 * @param {string} data.isCurrVersion - 是否当前版本
 * @param {string} data.remarks - 备注
 * @param {string} data.trainingRange - 训练范围
 * @param {string} data.trainingTime - 训练时间
 * @param {string} data.updateBy - 更新人
 * @param {string} data.updateDate - 更新日期
 * @param {string} data.version - 版本
 * @returns {Promise<any>} 返回更新结果
 */
export function updateCurrVersion(data) {
  return instance.post('/rtEao/updateCurrVersion', data);
}

/**
 * 统计科室ID数量
 * @param {Object} data - 请求参数对象
 * @param {number} data.beginTimestamp - 开始时间戳
 * @param {number} data.endTimestamp - 结束时间戳
 * @param {number} data.logTimestamp - 日志时间戳
 * @param {Array<string>} data.officeIds - 科室ID列表
 * @param {string} data.pathogenType - 病原体类型
 * @param {string} data.sn - 序列号
 * @returns {Promise<import('types/api/index').TResult<{
 *   officeId: string,
 *   officeName: string,
 *   deviceCountList: Array<{
 *     total: number,
 *     bad: number,
 *     sn: string
 *   }>,
 *   pathogenCountList: Array<{
 *     dt: string,
 *     pathogenName: string,
 *     bad: number
 *   }>
 * }>>} 返回一个包含科室ID、科室名称、设备数量列表和病原体数量列表的对象
 * example:
 * {
 *   "officeId": "86e8999bef3e480e8604df566c60d053",
 *   "officeName": "西单地铁",
 *   "deviceCountList": [
 *     {
 *       "total": 81,
 *       "bad": 18,
 *       "sn": "XD01"
 *     }
 *   ],
 *   "pathogenCountList": [
 *     {
 *       "dt": "2024-02-02",
 *       "pathogenName": "乙型流感病毒",
 *       "bad": 1
 *     }
 *   ]
 * }
 */
export function getStatDataByOfficeId(data) {
  return instance.post('/rtEao/statOfficeIdCount', data);
}

/**
 * 获取列表数据
 * @param {Object} data - 请求参数对象
 * @param {number} data.beginTimestamp - 开始时间戳
 * @param {number} data.endTimestamp - 结束时间戳
 * @param {number} data.limit - 每页数量
 * @param {Array<string>} data.officeIds - 科室ID列表
 * @param {number} data.offset - 偏移量
 * @returns {Promise<import('types/api/index').TPageResult<{
 *   sampleStartTime: string,
 *   sampleEndTime: string,
 *   sn: string,
 *   pathogenValue: number,
 *   pathogenName: string,
 *   pathogenState: string
 * }>} 返回一个包含样本开始时间、结束时间、序列号、病原体值、病原体名称和病原体状态的数组
 * example:
 * [
 *   {
 *     "sampleStartTime": "2022-12-10 10:00:00",
 *     "sampleEndTime": "2022-12-10 14:00:00",
 *     "sn": "BD1HX02",
 *     "pathogenValue": 38.91,
 *     "pathogenName": "新型冠状病毒",
 *     "pathogenState": "1"
 *   }
 * ]
 */
export function getNodeDetectList(data) {
  return instance.post('/rtEao/queryList', data);
}

/**
 * 获取病原体类型列表
 * @returns {Promise<import('types/api/index').TResult<{
 *   pathogenType: string,
 *   pathogenTypeName: string,
 *   isMangeType: string
 * }[]>>} 返回一个包含病原体类型、病原体类型名称和是否为管理类型的数组
 * example:
 * [
 *   {
 *     "pathogenType": "1",
 *     "pathogenTypeName": "新型冠状病毒",
 *     "isMangeType": "1"
 *   }
 * ]
 */
export function getPathogenTypeList() {
  return instance.post('/rtEao/queryPathogenTypeList');
}
